import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface Profile {
  id: string;
  name: string;
  bio: string | null;
  avatar_url: string | null;
  profile_image_1: string | null;
  profile_image_2: string | null;
  profile_image_3: string | null;
  renaissance_score: number;
  level: string;
  rating: number;
  sessions_completed: number;
  hours_learned: number;
  is_available: boolean;
  facebook_url: string | null;
  instagram_url: string | null;
  twitter_url: string | null;
  linkedin_url: string | null;
  snapchat_url: string | null;
  gender: string | null;
  location: string | null;
  interested_in: string[] | null;
  here_for: string[] | null;
  show_preferences: boolean;
  show_video_call_option: boolean;
}

export const useProfile = (userId?: string) => {
  const { user } = useAuth();
  const targetUserId = userId || user?.id;
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!targetUserId) {
      setLoading(false);
      return;
    }

    const fetchProfile = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', targetUserId)
          .single();

        if (error) throw error;
        setProfile(data);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();

    // Set up realtime subscription
    const channel = supabase
      .channel(`profile-changes-${targetUserId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${targetUserId}`,
        },
        (payload) => {
          if (payload.new) {
            setProfile(payload.new as Profile);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [targetUserId]);

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) return { error: 'Not authenticated' };

    try {
      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);

      if (error) throw error;
      return { error: null };
    } catch (err: any) {
      return { error: err.message };
    }
  };

  const updateAvailability = async (isAvailable: boolean) => {
    return updateProfile({ is_available: isAvailable });
  };

  return {
    profile,
    loading,
    error,
    updateProfile,
    updateAvailability,
  };
};
